import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IChangePasswordState {
  readonly error?: string
  readonly isOpen: boolean
  readonly isSaving: boolean
  readonly isValid: boolean
  readonly newPassword: string
  readonly oldPassword: string
  readonly verifyPassword: string
}

const ChangePasswordStateRecord = Record({
  error: undefined,
  isOpen: false,
  isSaving: false,
  isValid: false,
  newPassword: '',
  oldPassword: '',
  verifyPassword: '',
})

export class ChangePasswordState extends ChangePasswordStateRecord
  implements IChangePasswordState {
  public static fromJS(initialState?: Partial<IChangePasswordState>) {
    return new ChangePasswordState(initialState)
  }

  public readonly error?: string
  public readonly isOpen: boolean
  public readonly isSaving: boolean
  public readonly isValid: boolean
  public readonly newPassword: string
  public readonly oldPassword: string
  public readonly verifyPassword: string

  constructor(props?: Partial<ChangePasswordState>) {
    props ? super(props) : super()
  }

  public validate(): ChangePasswordState {
    const isValid =
      this.newPassword &&
      this.oldPassword &&
      this.verifyPassword &&
      this.newPassword === this.verifyPassword
    return this.set('isValid', isValid) as ChangePasswordState
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  CloseChangePasswordModal = 'CHANGE_PASSWORD::CLOSE_CHANGE_PASSWORD_MODAL',
  ResetState = 'CHANGE_PASSWORD::RESET_STATE',
  SetNewPassword = 'CHANGE_PASSWORD::SET_NEW_PASSWORD',
  SetOldPassword = 'CHANGE_PASSWORD::SET_OLD_PASSWORD',
  SetVerifyPassword = 'CHANGE_PASSWORD::SET_VERIFY_PASSWORD',
  ShowChangePasswordModal = 'CHANGE_PASSWORD::SHOW_CHANGE_PASSWORD_MODAL',
  UpdatePassword = 'CHANGE_PASSWORD::UPDATE_PASSWORD',
  UpdatePasswordFailure = 'CHANGE_PASSWORD::UPDATE_PASSWORD_FAILURE',
  UpdatePasswordRequest = 'CHANGE_PASSWORD::UPDATE_PASSWORD_REQUEST',
  UpdatePasswordSuccess = 'CHANGE_PASSWORD::UPDATE_PASSWORD_SUCCESS',
}

interface ICloseChangePasswordModalAction {
  type: ActionTypes.CloseChangePasswordModal
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetNewPasswordAction {
  newPassword: string
  type: ActionTypes.SetNewPassword
}

interface ISetOldPasswordAction {
  oldPassword: string
  type: ActionTypes.SetOldPassword
}

interface ISetVerifyPasswordAction {
  verifyPassword: string
  type: ActionTypes.SetVerifyPassword
}

interface IShowChangePasswordModalAction {
  type: ActionTypes.ShowChangePasswordModal
}

interface IUpdatePasswordAction {
  newPassword: string
  oldPassword: string
  type: ActionTypes.UpdatePassword
  userId: string
}

interface IUpdatePasswordFailureAction {
  error: string
  type: ActionTypes.UpdatePasswordFailure
}

interface IUpdatePasswordRequestAction {
  type: ActionTypes.UpdatePasswordRequest
}

interface IUpdatePasswordSuccessAction {
  type: ActionTypes.UpdatePasswordSuccess
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseChangePasswordModalAction
  | IResetStateAction
  | ISetNewPasswordAction
  | ISetOldPasswordAction
  | ISetVerifyPasswordAction
  | IShowChangePasswordModalAction
  | IUpdatePasswordFailureAction
  | IUpdatePasswordRequestAction
  | IUpdatePasswordSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closeChangePasswordModal(): ICloseChangePasswordModalAction {
  return { type: ActionTypes.CloseChangePasswordModal }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setNewPassword(newPassword: string): ISetNewPasswordAction {
  return { type: ActionTypes.SetNewPassword, newPassword }
}

function setOldPassword(oldPassword: string): ISetOldPasswordAction {
  return { type: ActionTypes.SetOldPassword, oldPassword }
}

function setVerifyPassword(verifyPassword: string): ISetVerifyPasswordAction {
  return { type: ActionTypes.SetVerifyPassword, verifyPassword }
}

function showChangePasswordModal(): IShowChangePasswordModalAction {
  return { type: ActionTypes.ShowChangePasswordModal }
}

function updatePassword(
  userId: string,
  newPassword: string,
  oldPassword: string,
): IUpdatePasswordAction {
  return {
    newPassword,
    oldPassword,
    type: ActionTypes.UpdatePassword,
    userId,
  }
}

function updatePasswordFailure(error: string): IUpdatePasswordFailureAction {
  return {
    error,
    type: ActionTypes.UpdatePasswordFailure,
  }
}

function updatePasswordRequest(): IUpdatePasswordRequestAction {
  return { type: ActionTypes.UpdatePasswordRequest }
}

function updatePasswordSuccess(): IUpdatePasswordSuccessAction {
  return { type: ActionTypes.UpdatePasswordSuccess }
}

export const actionCreators = {
  closeChangePasswordModal,
  resetState,
  setNewPassword,
  setOldPassword,
  setVerifyPassword,
  updatePassword,
}

export const changePasswordActionCreators = {
  showChangePasswordModal,
}

// -------------------------
// REDUCER
// -------------------------
export const reducer: Reducer<ChangePasswordState> = (
  state: ChangePasswordState = new ChangePasswordState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.CloseChangePasswordModal:
      return state.set('isOpen', false) as ChangePasswordState

    case ActionTypes.ResetState:
      return new ChangePasswordState()

    case ActionTypes.SetNewPassword:
      return (state.set(
        'newPassword',
        action.newPassword,
      ) as ChangePasswordState).validate()

    case ActionTypes.SetOldPassword:
      return (state.set(
        'oldPassword',
        action.oldPassword,
      ) as ChangePasswordState).validate()

    case ActionTypes.SetVerifyPassword:
      return (state.set(
        'verifyPassword',
        action.verifyPassword,
      ) as ChangePasswordState).validate()

    case ActionTypes.ShowChangePasswordModal:
      return state.set('isOpen', true) as ChangePasswordState

    case ActionTypes.UpdatePasswordFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as ChangePasswordState

    case ActionTypes.UpdatePasswordRequest:
      return state.set('isSaving', true) as ChangePasswordState

    case ActionTypes.UpdatePasswordSuccess:
      return state
        .set('isOpen', false)
        .set('isSaving', false) as ChangePasswordState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* updatePasswordSaga(action: IUpdatePasswordAction) {
  try {
    yield put(updatePasswordRequest())

    const reqUrl = `/api/Users/Password/${action.userId}`

    const reqOpts = {
      body: JSON.stringify({
        newPassword: action.newPassword,
        oldPassword: action.oldPassword,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(updatePasswordSuccess())
      yield put(successToast('Success!', 'Your password has been changed'))
    } else {
      yield put(updatePasswordFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(updatePasswordFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchUpdatePassword() {
  yield takeLatest(ActionTypes.UpdatePassword, updatePasswordSaga)
}

export function* changePasswordRootSaga() {
  yield fork(watchUpdatePassword)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.changePassword

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsOpen = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isOpen,
  )

export const selectIsSaving = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isSaving,
  )

export const selectIsValid = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isValid,
  )

export const selectNewPassword = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.newPassword,
  )

export const selectOldPassword = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.oldPassword,
  )

export const selectVerifyPassword = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.verifyPassword,
  )
